import axiosClient from "utils/axios";
import { ApiList, ApiResponse } from "../../../types/api.types";
import {
  AddAttendanceRequest,
  AttendanceDetails,
  AttendanceListRow,
  AttendanceReportRow,
  AttendanceSheet,
  AttendanceSheetHoliday,
  ClockInRequest,
  ClockOutRequest,
  ClockStatusListRow,
  EmployeeAttendance,
} from "features/EmployeeAttendance/types/employee-attendance.types";

const parseJsonImages = (raw: unknown): string[] | null => {
  if (raw == null) return null;
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [raw];
    } catch {
      return [raw];
    }
  }
  return null;
};

function normalizeAttendanceDetails(
  row: EmployeeAttendance,
): EmployeeAttendance {
  const r = row as EmployeeAttendance & {
    leave_application_id?: unknown;
    in_time?: unknown;
    out_time?: unknown;
    overtime_minutes?: unknown;
    location_type?: unknown;
    images?: unknown;
    out_images?: unknown;
  };
  const raw = r.leaveApplicationId ?? r.leave_application_id;
  const inTime = (r.inTime ?? r.in_time ?? null) as string | null;
  const outTime = (r.outTime ?? r.out_time ?? null) as string | null;
  const overtimeRaw = r.overtimeMinutes ?? r.overtime_minutes;
  const overtimeNumber =
    overtimeRaw == null || overtimeRaw === ""
      ? null
      : Number.isFinite(Number(overtimeRaw))
        ? Number(overtimeRaw)
        : null;
  const locationType = (r.locationType ??
    r.location_type ??
    null) as EmployeeAttendance["locationType"];
  const images = parseJsonImages(r.images);
  const outImages = parseJsonImages(
    (r as { out_images?: unknown }).out_images ?? r.outImages,
  );

  if (raw == null || raw === "") {
    return {
      ...row,
      leaveApplicationId: null,
      locationType,
      images,
      outImages,
      inTime,
      outTime,
      overtimeMinutes: overtimeNumber,
    };
  }
  const n = Number(raw);
  return {
    ...row,
    leaveApplicationId: Number.isFinite(n) && n > 0 ? n : null,
    locationType,
    images,
    outImages,
    inTime,
    outTime,
    overtimeMinutes: overtimeNumber,
  };
}

const buildClockInFormData = (payload: ClockInRequest) => {
  const formData = new FormData();

  if (payload.employeeId != null) {
    formData.append("employeeId", String(payload.employeeId));
  }
  if (payload.inTime) {
    formData.append("inTime", payload.inTime);
  }
  if (payload.inLocation) {
    formData.append("inLocation", payload.inLocation);
  }
  if (payload.inLocationType) {
    formData.append("inLocationType", payload.inLocationType);
  }
  if (payload.remarks) {
    formData.append("remarks", payload.remarks);
  }
  if (payload.images?.length) {
    payload.images.forEach((item) => {
      if (item instanceof File) {
        formData.append("images", item);
      } else if (typeof item === "string" && item.trim()) {
        formData.append("images", item.trim());
      }
    });
  }

  return formData;
};

export type AttendanceSheetListData = ApiList<AttendanceSheet[]> & {
  daysInMonth?: number;
  holidays?: AttendanceSheetHoliday[];
};

export const getAttendanceSheet = async ({
  page,
  limit,
  offset,
  month,
  year,
  search,
  employeeId,
}: {
  page?: number;
  limit?: number;
  offset?: number;
  month?: number;
  year?: number;
  search?: string;
  employeeId?: number;
} = {}) => {
  try {
    const response = await axiosClient.post<
      ApiResponse<AttendanceSheetListData>
    >("/employee-attendance/attendance-sheet", {
      page,
      limit,
      offset,
      month,
      year,
      ...(employeeId != null ? { employeeId } : {}),
      ...(search?.trim() ? { search: search.trim() } : {}),
    });
    return response.data;
  } catch (err) {
    console.warn("Error fetching attendance sheet : ", err);
    throw err;
  }
};

export const createAttendance = async (payload: AddAttendanceRequest) => {
  try {
    const response = await axiosClient.post<ApiResponse<AddAttendanceRequest>>(
      "/employee-attendance",
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating attendance : ", err);
    throw err;
  }
};

export const clockInAttendance = async (payload: ClockInRequest = {}) => {
  try {
    const formData = buildClockInFormData(payload);
    const response = await axiosClient.post<ApiResponse<EmployeeAttendance>>(
      "/employee-attendance/clock-in",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  } catch (err) {
    console.warn("Error clocking in : ", err);
    throw err;
  }
};

const buildClockOutFormData = (payload: ClockOutRequest) => {
  const formData = new FormData();

  if (payload.employeeId != null) {
    formData.append("employeeId", String(payload.employeeId));
  }
  if (payload.outTime) {
    formData.append("outTime", payload.outTime);
  }
  if (payload.outLocation) {
    formData.append("outLocation", payload.outLocation);
  }
  if (payload.outLocationType) {
    formData.append("outLocationType", payload.outLocationType);
  }
  if (payload.remarks) {
    formData.append("remarks", payload.remarks);
  }
  if (payload.outImages?.length) {
    payload.outImages.forEach((item) => {
      if (item instanceof File) {
        formData.append("files", item);
      } else if (typeof item === "string" && item.trim()) {
        formData.append("files", item.trim());
      }
    });
  }

  return formData;
};

export const clockOutAttendance = async (payload: ClockOutRequest = {}) => {
  try {
    const formData = buildClockOutFormData(payload);
    const response = await axiosClient.post<ApiResponse<EmployeeAttendance>>(
      "/employee-attendance/clock-out",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  } catch (err) {
    console.warn("Error clocking out : ", err);
    throw err;
  }
};

export const getAttendance = async ({
  employeeId,
  date,
}: {
  employeeId: number;
  date: string;
}) => {
  try {
    if (!Number.isInteger(employeeId) || employeeId <= 0) {
      throw new Error("Invalid employee ID");
    }
    const response = await axiosClient.post<ApiResponse<AttendanceDetails>>(
      `/employee-attendance/details`,
      { employeeId, date },
    );
    const body = response.data;
    if (!body.data) return body;
    const raw = body.data as AttendanceDetails & { sessions?: EmployeeAttendance[] };
    const sessions = Array.isArray(raw.sessions)
      ? raw.sessions.map(normalizeAttendanceDetails)
      : [];
    return {
      ...body,
      data: { ...normalizeAttendanceDetails(body.data), sessions } as AttendanceDetails,
    };
  } catch (err) {
    console.warn("Error fetching attendance details : ", err);
    throw err;
  }
};

export const getAttendanceReport = async ({
  month,
  year,
  departmentId,
  employeeId,
  page,
  limit,
}: {
  month: number;
  year: number;
  departmentId?: number | null;
  employeeId?: number | null;
  page?: number;
  limit?: number;
}) => {
  const response = await axiosClient.get<
    ApiResponse<ApiList<AttendanceReportRow[]>>
  >("/employee-attendance/report", {
    params: {
      month,
      year,
      departmentId: departmentId ?? undefined,
      employeeId: employeeId ?? undefined,
      page,
      limit,
    },
  });
  return response.data;
};

export const deleteAttendance = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<EmployeeAttendance>>(
      `/employee-attendance`,
      { data: { id } },
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting attendance : ", err);
    throw err;
  }
};

export const getClockStatusList = async ({
  date,
  departmentId,
  page,
  limit,
  search,
}: {
  date?: string;
  departmentId?: number;
  page?: number;
  limit?: number;
  search?: string;
} = {}) => {
  const response = await axiosClient.get<
    ApiResponse<ApiList<ClockStatusListRow[]>>
  >("/employee-attendance/clock-status", {
    params: {
      date: date || undefined,
      departmentId: departmentId ?? undefined,
      page,
      limit,
      ...(search?.trim() ? { search: search.trim() } : {}),
    },
  });
  return response.data;
};

export const getAttendanceList = async ({
  date,
  status,
  inLocationType,
  employeeId,
  departmentId,
  page,
  limit,
  search,
}: {
  date?: string;
  status?: string;
  inLocationType?: string;
  employeeId?: number;
  departmentId?: number;
  page?: number;
  limit?: number;
  search?: string;
} = {}) => {
  const response = await axiosClient.get<
    ApiResponse<ApiList<AttendanceListRow[]>>
  >("/employee-attendance", {
    params: {
      date: date || undefined,
      status: status || undefined,
      inLocationType: inLocationType || undefined,
      employeeId: employeeId ?? undefined,
      departmentId: departmentId ?? undefined,
      page,
      limit,
      ...(search?.trim() ? { search: search.trim() } : {}),
    },
  });
  return response.data;
};
